import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { wallToInstant, instantToWall, localTimezone, tzLabel } from '../utils/timezone';

const API = process.env.REACT_APP_API_URL || '';
const SNAP = 15; // minutes
const BRAND = '#185F30';

const fmt = (m) => { const h = Math.floor(m / 60), mm = m % 60, ap = h >= 12 ? 'PM' : 'AM', hh = ((h + 11) % 12) + 1; return `${hh}:${String(mm).padStart(2, '0')} ${ap}`; };
const toMin = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + m; };
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * AvailabilityPicker — public tokenized page at /availability/:token.
 * Recruiter offers broad windows; candidate marks free sub-ranges within each
 * on a SHARED day axis (so an hour is the same width across all rows), or
 * flags a window "flexible all day".
 *
 * proposed: [{ id, date, start:'HH:MM', end:'HH:MM' }]
 * response.selected: { ranges:[{ windowId, date, start:'HH:MM', end:'HH:MM' }], flexible:[windowId] }
 */
export default function AvailabilityPicker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [ranges, setRanges] = useState([]); // { id, windowId, s, e } minutes
  const [flexible, setFlexible] = useState(() => new Set()); // windowId
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const uidRef = useRef(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/availability/public/${token}`);
        if (r.status === 404) return setState('notfound');
        if (r.status === 410) return setState('expired');
        if (!r.ok) return setState('error');
        const d = await r.json();
        setData(d);
        // Restore prior response if present.
        const sel = d.selected && typeof d.selected === 'object' && !Array.isArray(d.selected) ? d.selected : {};
        if (Array.isArray(sel.ranges)) {
          setRanges(sel.ranges.map(x => ({ id: uidRef.current++, windowId: x.windowId, s: toMin(x.start), e: toMin(x.end) })));
        }
        if (Array.isArray(sel.flexible)) setFlexible(new Set(sel.flexible));
        setNote(d.candidate_note || '');
        setState('ready');
      } catch { setState('error'); }
    })();
  }, [token]);

  const viewerTz = localTimezone();
  const sourceTz = data?.timezone || viewerTz;

  // Convert each proposed window from the request's tz to the viewer's local
  // tz for display + selection. Keep the original id/date so we can map back.
  const windows = useMemo(() => {
    const raw = Array.isArray(data?.proposed) ? data.proposed : [];
    return raw.map(w => {
      const sInst = wallToInstant(w.date, w.start, sourceTz);
      const eInst = wallToInstant(w.date, w.end, sourceTz);
      const sLocal = instantToWall(sInst, viewerTz);
      const eLocal = instantToWall(eInst, viewerTz);
      return {
        id: w.id,
        srcDate: w.date,
        date: sLocal.date,          // local calendar date
        start: sLocal.hhmm,          // local HH:MM
        end: eLocal.hhmm,
        _sInst: sInst, _eInst: eInst,
      };
    });
  }, [data, sourceTz, viewerTz]);

  // Shared axis across all windows (padded to the hour).
  const axis = useMemo(() => {
    if (!windows.length) return { start: 9 * 60, end: 17 * 60 };
    const start = Math.floor(Math.min(...windows.map(w => toMin(w.start))) / 60) * 60;
    const end = Math.ceil(Math.max(...windows.map(w => toMin(w.end))) / 60) * 60;
    return { start, end };
  }, [windows]);

  // Group windows by day for headers.
  const byDay = useMemo(() => {
    const m = new Map();
    windows.forEach(w => {
      if (!m.has(w.date)) m.set(w.date, []);
      m.get(w.date).push(w);
    });
    return Array.from(m.entries()).map(([date, wins]) => ({ date, wins }));
  }, [windows]);

  const addRange = useCallback((windowId, s, e) => {
    const id = uidRef.current++;
    setRanges(prev => [...prev, { id, windowId, s, e }]);
    return id;
  }, []);
  const updRange = useCallback((id, patch) => setRanges(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r)), []);
  const delRange = useCallback((id) => setRanges(prev => prev.filter(r => r.id !== id)), []);
  const toggleFlex = useCallback((windowId) => {
    setFlexible(prev => { const n = new Set(prev); n.has(windowId) ? n.delete(windowId) : n.add(windowId); return n; });
    if (!flexible.has(windowId)) setRanges(prev => prev.filter(r => r.windowId !== windowId));
  }, [flexible]);

  const totalMarked = ranges.length + flexible.size;

  const submit = async () => {
    setSubmitting(true);
    try {
      const winById = Object.fromEntries(windows.map(w => [w.id, w]));
      const payloadRanges = ranges.map(r => {
        const w = winById[r.windowId];
        // r.s/r.e are minutes in the viewer's local tz on w.date. Convert the
        // instant back to the source (meeting) tz so the recruiter sees the
        // time in the meeting's timezone.
        const localDate = w?.date;
        const sInst = wallToInstant(localDate, toHHMM(r.s), viewerTz);
        const eInst = wallToInstant(localDate, toHHMM(r.e), viewerTz);
        const sSrc = instantToWall(sInst, sourceTz);
        const eSrc = instantToWall(eInst, sourceTz);
        return { windowId: r.windowId, date: sSrc.date, start: sSrc.hhmm, end: eSrc.hhmm };
      });
      const r = await fetch(`${API}/api/availability/public/${token}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected: payloadRanges, flexible: Array.from(flexible), note }),
      });
      if (!r.ok) throw new Error();
      setState('done');
    } catch { setState('error'); } finally { setSubmitting(false); }
  };

  if (state === 'loading') return <Shell data={data}><div className="py-20 text-center text-gray-400">Loading…</div></Shell>;
  if (state === 'notfound') return <Shell data={data}><Message title="Link not found" body="This availability link is invalid or has been removed." /></Shell>;
  if (state === 'expired') return <Shell data={data}><Message title="Link expired" body="This request is no longer active. Please contact your recruiter for a new link." /></Shell>;
  if (state === 'error') return <Shell data={data}><Message title="Something went wrong" body="Please try again in a moment." /></Shell>;
  if (state === 'done') return (
    <Shell data={data}>
      <div className="py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Thank you!</h2>
        <p className="text-gray-500 mt-2">Your availability has been sent. {data?.recruiter_name || 'Your recruiter'} will follow up to confirm a time.</p>
      </div>
    </Shell>
  );

  return (
    <Shell data={data}>
      <p className="av-muted text-[11px] text-center mt-3 mb-5 text-gray-400">Times shown in your timezone · {tzLabel(viewerTz)}</p>

      {windows.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-6">No times have been proposed yet. Please contact your recruiter.</p>
      ) : (
        <div className="space-y-6">
          {byDay.map(({ date, wins }) => (
            <div key={date}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-gray-800">{new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</span>
              </div>
              {wins.map(w => (
                <WindowTrack
                  key={w.id}
                  win={w}
                  axis={axis}
                  ranges={ranges.filter(r => r.windowId === w.id)}
                  flexible={flexible.has(w.id)}
                  onAdd={addRange}
                  onUpdate={updRange}
                  onDelete={delRange}
                  onWhole={() => { setRanges(prev => prev.filter(r => r.windowId !== w.id)); addRange(w.id, toMin(w.start), toMin(w.end)); }}
                  onToggleFlex={() => toggleFlex(w.id)}
                />
              ))}
            </div>
          ))}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Anything to add? (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="e.g. I have a hard stop at 4pm on Tuesdays."
              className="av-input mt-1.5 w-full text-sm border border-gray-200 rounded px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
          </div>
        </div>
      )}

      <div className="av-card av-border mt-6 -mx-6 sm:-mx-8 px-6 sm:px-8 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
        <button onClick={submit} disabled={submitting || totalMarked === 0}
          className="w-full px-6 py-2.5 text-sm font-semibold text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: BRAND }}>
          {submitting ? 'Sending…' : data.status === 'responded' ? 'Update availability' : 'Send availability'}
        </button>
      </div>
    </Shell>
  );
}

function WindowTrack({ win, axis, ranges, flexible, onAdd, onDelete, onWhole, onToggleFlex }) {
  const winStart = toMin(win.start), winEnd = toMin(win.end);
  const span = axis.end - axis.start || 1;
  const pct = (m) => ((m - axis.start) / span) * 100;

  // Dropdown time options within the window, at SNAP intervals.
  const startOpts = [];
  for (let m = winStart; m < winEnd; m += SNAP) startOpts.push(m);
  const endOptsFor = (s) => { const o = []; for (let m = s + SNAP; m <= winEnd; m += SNAP) o.push(m); return o; };

  const [draftStart, setDraftStart] = React.useState(winStart);
  const [draftEnd, setDraftEnd] = React.useState(Math.min(winStart + 60, winEnd));

  const ticks = [];
  for (let m = axis.start; m <= axis.end; m += 60) ticks.push(m);

  const addDraft = () => {
    let s = draftStart, e = draftEnd;
    if (e <= s) e = Math.min(s + SNAP, winEnd);
    onAdd(win.id, s, e);
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] text-gray-400">available {fmt(winStart)} – {fmt(winEnd)}</span>
        {!flexible && <button onClick={onWhole} className="text-[11px] font-medium text-emerald-700 hover:text-emerald-800 border border-emerald-200 bg-emerald-50 rounded px-2 py-0.5">Whole window</button>}
      </div>

      {/* Read-only visual bar — solid blocks fill the chosen ranges. */}
      <div className="av-track relative rounded border overflow-hidden" style={{ height: 40, opacity: flexible ? 0.45 : 1 }}>
        {/* hatched = outside the offered window */}
        <div className="av-hatch absolute top-0 bottom-0" style={{ left: 0, width: pct(winStart) + '%' }} />
        <div className="av-hatch absolute top-0 bottom-0" style={{ left: pct(winEnd) + '%', right: 0 }} />
        {/* subtle hour gridlines */}
        {ticks.map(m => (
          <div key={m} className="av-tick absolute top-0 bottom-0" style={{ left: pct(m) + '%', width: 1 }} />
        ))}
        {/* solid chosen blocks — flush to the bar's top/bottom border */}
        {ranges.map(r => (
          <div key={r.id} className="av-block absolute" style={{ top: -1, bottom: -1, left: pct(r.s) + '%', width: (pct(r.e) - pct(r.s)) + '%' }} />
        ))}
      </div>
      {/* hour labels BELOW the bar; edge labels clamp inside */}
      <div className="relative h-4 mt-0.5">
        {ticks.map((m, i) => {
          const edgeStyle = i === 0 ? { left: '0%' } : i === ticks.length - 1 ? { right: '0%' } : { left: pct(m) + '%', transform: 'translateX(-50%)' };
          return <span key={m} className="av-muted absolute text-[9px] text-gray-400" style={edgeStyle}>{fmt(m).replace(':00', '')}</span>;
        })}
      </div>

      {/* Dropdown controls */}
      {!flexible && (
        <div className="mt-2 space-y-1.5">
          {ranges.slice().sort((a, b) => a.s - b.s).map(r => (
            <div key={r.id} className="flex items-center gap-1.5 text-xs">
              <span className="av-block w-2.5 h-2.5 rounded-sm flex-shrink-0" />
              <span className="text-gray-700 font-medium flex-1">{fmt(r.s)} – {fmt(r.e)}</span>
              <button onClick={() => onDelete(r.id)} className="text-gray-300 hover:text-red-500 p-0.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-500">Free</span>
            <select value={draftStart} onChange={(e) => { const v = +e.target.value; setDraftStart(v); if (draftEnd <= v) setDraftEnd(Math.min(v + SNAP, winEnd)); }}
              className="av-input text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500">
              {startOpts.map(m => <option key={m} value={m}>{fmt(m)}</option>)}
            </select>
            <span className="text-gray-400 text-xs">to</span>
            <select value={draftEnd} onChange={(e) => setDraftEnd(+e.target.value)}
              className="av-input text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500">
              {endOptsFor(draftStart).map(m => <option key={m} value={m}>{fmt(m)}</option>)}
            </select>
            <button onClick={addDraft} className="text-[11px] font-medium text-white bg-emerald-600 rounded px-2.5 py-1.5 hover:bg-emerald-700">Add</button>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 cursor-pointer select-none pt-0.5">
            <input type="checkbox" checked={flexible} onChange={onToggleFlex} className="accent-emerald-600 w-3 h-3" /> I'm flexible all day
          </label>
        </div>
      )}
      {flexible && (
        <div className="mt-2 space-y-2">
          <div className="text-xs text-gray-600"><span className="font-medium text-emerald-700">Flexible all day</span> — open to any time</div>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={flexible} onChange={onToggleFlex} className="accent-emerald-600 w-3 h-3" /> I'm flexible all day
          </label>
        </div>
      )}
    </div>
  );
}


function Shell({ children, data }) {
  // Detect the viewer's dark-mode preference and apply a class, so styling is
  // reliable even where the media query alone misbehaves. Reacts to changes.
  const [isDark, setIsDark] = useState(() => {
    try { return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
  });
  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const on = (e) => setIsDark(e.matches);
      mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
      return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
    } catch {}
  }, []);
  // Only use the permanent base64 logo on the public page. LinkedIn logo_url
  // values are signed and expire (403), so we never render them here — fall
  // back to the firm name as text until a logo is uploaded via the firm profile.
  const clientLogo = data?.client_logo_data || null;
  const clientName = data?.client_firm_name;
  const position = data?.pipeline_name || data?.title;
  const recFirm = data?.recruiter_firm_name;
  const recLogo = data?.recruiter_logo_data || null;
  return (
    <div className={`avail-page ${isDark ? 'avail-dark' : ''} min-h-screen bg-gray-100 flex items-start justify-center py-8 px-4`}>
      <style>{`
        .avail-page .av-block { background:#D1FAE5; }
        .avail-page .av-track { background:#fff; border-color:#d1d5db; }
        .avail-page .av-hatch { background:repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 4px,#e9edf2 4px,#e9edf2 8px); }
        .avail-page .av-tick { background:#eef2f6; }

        html[data-theme="dark"] .avail-page,
        .avail-page.avail-dark { background:#0d1210 !important; }
        html[data-theme="dark"] .avail-page .av-card, .avail-page.avail-dark .av-card { background:#151b18 !important; border-color:#232c27 !important; }
        html[data-theme="dark"] .avail-page .av-border, .avail-page.avail-dark .av-border { border-color:#232c27 !important; }
        html[data-theme="dark"] .avail-page .av-title, .avail-page.avail-dark .av-title { color:#e5e7eb !important; }
        html[data-theme="dark"] .avail-page .av-sub, .avail-page.avail-dark .av-sub { color:#9aa4a0 !important; }
        html[data-theme="dark"] .avail-page .av-muted, .avail-page.avail-dark .av-muted { color:#6b7280 !important; }
        html[data-theme="dark"] .avail-page .av-track, .avail-page.avail-dark .av-track { background:#111714 !important; border-color:#2a332e !important; }
        html[data-theme="dark"] .avail-page .av-hatch, .avail-page.avail-dark .av-hatch { background:repeating-linear-gradient(45deg,#141a17,#141a17 4px,#181f1b 4px,#181f1b 8px) !important; }
        html[data-theme="dark"] .avail-page .av-tick, .avail-page.avail-dark .av-tick { background:#1b221e !important; }
        html[data-theme="dark"] .avail-page .av-block, .avail-page.avail-dark .av-block { background:#2a5f43 !important; }
        html[data-theme="dark"] .avail-page .av-input, .avail-page.avail-dark .av-input { background:#111714 !important; border-color:#2a332e !important; color:#d1d5db !important; }
        html[data-theme="dark"] .avail-page .av-footer, .avail-page.avail-dark .av-footer { color:#4b5563 !important; }

        @media (prefers-color-scheme: dark) {
          .avail-page { background:#0d1210 !important; }
          .avail-page .av-card { background:#151b18 !important; border-color:#232c27 !important; }
          .avail-page .av-border { border-color:#232c27 !important; }
          .avail-page .av-title { color:#e5e7eb !important; }
          .avail-page .av-sub { color:#9aa4a0 !important; }
          .avail-page .av-muted { color:#6b7280 !important; }
          .avail-page .av-track { background:#111714 !important; border-color:#2a332e !important; }
          .avail-page .av-hatch { background:repeating-linear-gradient(45deg,#141a17,#141a17 4px,#181f1b 4px,#181f1b 8px) !important; }
          .avail-page .av-tick { background:#1b221e !important; }
          .avail-page .av-block { background:#2a5f43 !important; }
          .avail-page .av-input { background:#111714 !important; border-color:#2a332e !important; color:#d1d5db !important; }
          .avail-page .av-footer { color:#4b5563 !important; }
        }
      `}</style>
      <div className="av-card w-full max-w-md bg-white rounded shadow-sm border border-gray-100 overflow-hidden">
        <div className="av-border px-6 sm:px-8 pt-6 pb-4 text-center border-b border-gray-100">
          <div className="h-10 flex items-center justify-center mb-2">
            {clientLogo
              ? <img src={clientLogo} alt={clientName || 'Firm'} className="max-h-10 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : null}
          </div>
          {clientName && <p className="av-sub text-sm font-medium text-gray-500 mb-0.5">{clientName}</p>}
          {position && <h1 className="av-title text-lg font-bold text-gray-900">{position}</h1>}
          {recFirm && (
            <div className="av-sub mt-3 flex flex-col items-center gap-1 text-[10px] text-gray-400">
              <span className="uppercase tracking-wide">Managed by</span>
              {recLogo
                ? <img src={recLogo} alt={recFirm} className="h-6 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }} />
                : null}
              <span className="font-medium text-gray-500 text-[12px]" style={recLogo ? { display: 'none' } : {}}>{recFirm}</span>
            </div>
          )}
        </div>
        <div className="px-6 sm:px-8 py-5">
          {children}
        </div>
        <div className="av-border px-6 py-3 border-t border-gray-100">
          <p className="av-footer text-center text-[10px] text-gray-300">Powered by BG Terminal</p>
        </div>
      </div>
    </div>
  );
}

function Message({ title, body }) {
  return (
    <div className="py-14 text-center">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <p className="text-gray-500 mt-2">{body}</p>
    </div>
  );
}
